// prefillWhenRules — the pure decision engine behind `field.prefillWhen`
// ("Conditional Default" in Form Groups), shared by AddFormV1 and EditFormV1 so
// both forms can never drift apart on what a rule means.
//
// A prefillWhen rule says "when the field named `field` holds `value`, put
// `setValue` into THIS field" — e.g. contractType="W2" drops the default
// employer record id into employerId. Two admin-config extensions live here:
//
//   • rule.clearValue  — a matching rule CLEARS this field instead of setting
//     it (e.g. contractType="C2C" must not keep the W2 default employer
//     sitting there). Absent/false = today's set behaviour, so every rule ever
//     saved keeps working untouched.
//   • clearOnMismatch  — field-level (`field.prefillWhenReset`): once NO rule
//     matches any more, revert this field to empty rather than stranding the
//     default a rule previously applied.
//
// WHY the lastApplied bookkeeping: the form store cannot tell "this id is the
// W2 default we injected" from "the user deliberately picked this employer" —
// both are just a string in the field. So the caller remembers the exact value
// the last matching rule wrote, and a mismatch-clear only fires while the field
// STILL holds precisely that value. The moment the user overrides it, the value
// is theirs and we never touch it again. TRADEOFF, deliberately documented: a
// user who manually re-picks the very same record the rule had set is
// indistinguishable from the rule's own write, and that value WILL be cleared
// on mismatch. Clearing an identical value is the benign side of the trade —
// the alternative (never clearing) is the bug this exists to fix.
//
// WHY clears are suppressed on the FIRST evaluation: on the edit form the very
// first tick sees the record's own stored contractType. If that value matches a
// clearValue rule, an unguarded clear would wipe a stored employer the user
// never touched, purely from opening the form. A clear must always be the
// consequence of the user CHANGING the watched field, never of a page load.
// (On the add form the field is empty at that point, so nothing is lost.)
//
// Nothing here knows any module, field or value name — all of it is admin data.

const asKey = (v) => String(v ?? '');

/**
 * prefillWhenWatchFields — the distinct field keys a rule set references, in
 * rule order. Rules may point at DIFFERENT fields, so callers must watch each
 * one, not just the first rule's.
 *
 * @param {Array} rules  field.prefillWhen
 * @returns {string[]}
 */
export function prefillWhenWatchFields(rules) {
  const seen = new Set();
  const out = [];
  (Array.isArray(rules) ? rules : []).forEach((r) => {
    const key = r?.field;
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(key);
  });
  return out;
}

/**
 * prefillWhenWatchPaths — resolves each referenced field key to an absolute
 * antd name path using the SAME row-vs-top-level scoping `showIf` uses.
 *
 * Inside an addRow group, a rule referencing one of the group's OWN fields
 * resolves to [listName, rowIndex, key]; a rule referencing anything else (the
 * top-level contractType read from inside the repeatable employer rows) must
 * resolve to the top-level path. renderField's `prefixFor(key)` closure already
 * encodes exactly that decision — passing it in means a Conditional Default and
 * a Show Condition can never disagree about which field a key names. Getting
 * this wrong is what made "W2/C2C not working" the first time round.
 *
 * @param {Array} rules            field.prefillWhen
 * @param {Array|string} name      this field's own absolute name path
 * @param {Function} resolvePath   (name, key, prefixFor) => absolute path
 * @param {Function} [prefixFor]   renderField's showIf prefix resolver
 * @returns {{field: string, path: Array}[]}
 */
export function prefillWhenWatchPaths(rules, name, resolvePath, prefixFor) {
  return prefillWhenWatchFields(rules).map((field) => ({
    field,
    path: resolvePath(name, field, prefixFor),
  }));
}

/**
 * prefillWhenMatches — does THIS rule's condition hold right now?
 *
 * The single definition of "a rule matches", exported so the option side
 * (`prefillWhenExclusive`, see optionConstraints.js) can never drift from the
 * value side: an option is offered on exactly the ticks the rule would fire.
 *
 * @param {object} rule     one field.prefillWhen entry
 * @param {object} watched  { [referencedFieldKey]: liveValue }
 * @returns {boolean}
 */
export function prefillWhenMatches(rule, watched = {}) {
  if (!rule || !rule.field) return false;
  return asKey(watched[rule.field]) === asKey(rule.value);
}

/**
 * resolvePrefillWhen — decides what should happen to the target field on this
 * tick. Pure: it never touches the antd store, the caller applies the outcome.
 *
 * @param {object}  args
 * @param {Array}   args.rules             field.prefillWhen
 * @param {object}  args.watched           { [referencedFieldKey]: liveValue }
 * @param {*}       args.current           the target field's live value
 * @param {*}       args.lastApplied       value the last matching SET rule wrote (undefined = none)
 * @param {boolean} args.clearOnMismatch   field.prefillWhenReset (default on)
 * @param {boolean} args.initial           true on the very first evaluation
 * @returns {{action: 'set'|'clear'|'none', value?: *}}
 */
export function resolvePrefillWhen({
  rules,
  watched = {},
  current,
  lastApplied,
  clearOnMismatch = true,
  initial = false,
} = {}) {
  const list = Array.isArray(rules) ? rules : [];
  // First matching rule wins — same precedence the single-path watcher had.
  const match = list.find((r) => prefillWhenMatches(r, watched));

  if (match) {
    if (match.clearValue) {
      // A clear rule that fires on page load would delete stored data (see the
      // header comment), so the first tick only ever arms the watcher.
      return initial ? { action: 'none' } : { action: 'clear' };
    }
    return { action: 'set', value: match.setValue };
  }

  // No rule matches any more. Only revert a value WE put there.
  if (clearOnMismatch && lastApplied !== undefined && asKey(current) === asKey(lastApplied)) {
    return { action: 'clear' };
  }
  return { action: 'none' };
}
